import {action, computed, makeObservable, observable, values, when} from "mobx";
import {RootStore} from "modules/stores/RootStore";
import {filter, findIndex, isEmpty, isUndefined, keyBy, map, size} from "lodash";
import {Api, IAnswersPayload} from "modules/utils/Api";
import {QuestionType, RequestState} from "modules/enums";
import {createConnextraScriptTag, trackIdentify, ConnextraType} from "modules/utils";
import {AnswersUtils} from "modules/utils/answers";
import * as Sentry from "@sentry/react";

export class UIQuestionsProgress<TModel extends RootStore> {
	finalScreenID = Number.MAX_SAFE_INTEGER;
	resultScreenID = Number.MAX_SAFE_INTEGER - 1;
	registrationScreenID = Number.MAX_SAFE_INTEGER - 2;

	@observable isMenuOpen: boolean = false;
	@observable answers: Record<string, number> = AnswersUtils.get();
	@observable viewedQuestionID?: number;
	@observable activeQuestionIndex: number = 0;
	@observable apiState: RequestState = RequestState.IDLE;
	@observable errors: Record<string, string> = {};
	@observable isShowResultPage: boolean = false;

	constructor(
		private readonly rootStore: Pick<TModel, "questions" | "UISquadList" | "user" | "profile">
	) {
		makeObservable(this);

		when(
			() => this.isAllQuestionsAnswered && !!this.rootStore.profile.terms,
			() => {
				setTimeout(() => {
					void this.saveAnswers();
				}, 1000);
			}
		);
	}

	get isShowSupportedTeamCta() {
		const {user} = this.rootStore;

		return user?.favoriteTeam !== this.rootStore.UISquadList.topSquad?.id;
	}

	@computed get allItems() {
		const questions = this.rootStore.questions.entities;

		const activeIndex = this.activeQuestionIndex;

		return questions.map((question, index) => {
			const hasAnswer = !isUndefined(this.answers[question.id]);
			const isActive = activeIndex === index;
			const questionNumber = index + 1;

			return {hasAnswer, isActive, questionNumber, ...question};
		});
	}

	@computed get hasUser() {
		const {user, profile} = this.rootStore;
		return !!user.id && Boolean(profile.terms);
	}

	@computed get isLoggedIn() {
		const {user} = this.rootStore;
		return user.isLoggedIn;
	}

	@computed get isEmpty() {
		return isEmpty(this.allItems);
	}

	@computed get noOfQuestions() {
		return size(this.allItems);
	}

	@computed get allItemsByID() {
		return keyBy(this.allItems, "id");
	}

	@computed get progressItems() {
		const maxIndex = size(filter(this.allItems, {hasAnswer: true}));
		return this.allItems.filter((_, index) => index <= maxIndex);
	}

	@computed get isAllQuestionsAnswered() {
		return !isEmpty(this.allItems) && this.allItems.every(({hasAnswer}) => hasAnswer);
	}

	@computed get isScrollCTAVisibleToTop() {
		const screenId = this.hasUser ? this.finalScreenID : this.registrationScreenID;
		return this.isAllQuestionsAnswered && this.viewedQuestionID === screenId;
	}

	@computed get isAnyQuestionAnswered() {
		return !isEmpty(this.allItems) && this.allItems.some(({hasAnswer}) => hasAnswer);
	}

	@computed get isScrollCTAVisible() {
		if (this.viewedQuestionID === this.registrationScreenID) {
			return true;
		}
		if (this.viewedQuestionID === this.resultScreenID) {
			return true;
		}
		if (this.viewedQuestionID === this.finalScreenID) {
			return true;
		}
		return Boolean(this.viewedQuestionID && this.answers[this.viewedQuestionID]);
	}

	@computed
	private get nextQuestionID() {
		const nextQuestionIndex = findIndex(this.allItems, {id: this.viewedQuestionID}) + 1;
		if (nextQuestionIndex > 0 && this.allItems[nextQuestionIndex]?.id) {
			return this.allItems[nextQuestionIndex]?.id;
		}
		return this.hasUser ? this.resultScreenID : this.registrationScreenID;
	}

	@action toggleMenu(isOpen?: boolean) {
		if (isOpen !== undefined) {
			this.isMenuOpen = isOpen;
		} else {
			this.isMenuOpen = !this.isMenuOpen;
		}
	}

	scrollToNextScreen(id?: number, block?: ScrollLogicalPosition) {
		document.getElementById(`question-${id || this.nextQuestionID}`)?.scrollIntoView({
			block: block || "start",
			behavior: "smooth",
		});
	}

	@action selectAnswer(questionID: number, optionID: number) {
		const currentQuestionIndex = findIndex(this.allItems, {id: questionID});
		const nextQuestionIndex = currentQuestionIndex + 1;

		if (!this.activeQuestionIndex || this.activeQuestionIndex < nextQuestionIndex) {
			this.activeQuestionIndex = nextQuestionIndex;
		}

		if (this.answers[questionID] !== optionID) {
			this.answers[questionID] = optionID;
			setTimeout(() => this.scrollToNextScreen(), 500);
			AnswersUtils.save(this.answers);
		}
	}

	@action reportError(errors: Record<string, string>) {
		this.errors = errors;

		return true;
	}

	@action clearErrors() {
		this.errors = {};
	}

	private trackAnalytics = (data: Record<string, number>) => {
		const team_given = this.rootStore.UISquadList.topSquad?.id;
		const answers = map(data, (value, key) => ({
			question_id: key,
			answer_id: value,
		}));

		window.Trialfire.track("pick_submitted", {
			game: "team_quiz",
			answers,
			team_given,
		});

		window.rudderanalytics.track("pick_submitted", {
			game: "team_quiz",
			answers,
			team_given,
		});
	};

	async saveAnswers() {
		if (this.apiState === RequestState.Requested) {
			return;
		}

		const payload: IAnswersPayload = {
			favoriteTeam: this.rootStore.UISquadList.topSquad?.id,
			answers: values(this.answers) as number[],
		};

		this.apiState = RequestState.Requested;

		try {
			await Api.Answers.save(payload);
			this.isShowResultPage = true;
			AnswersUtils.clear();
			setTimeout(() => this.scrollToNextScreen(this.resultScreenID), 500);
			const userEmail = this.rootStore.user.email;
			const userId = this.rootStore.user.id;

			if (userEmail && userId) {
				trackIdentify(userEmail);
				createConnextraScriptTag(ConnextraType.SUBMISSION, userId);
			}

			this.trackAnalytics(this.answers);
		} catch (err) {
			Sentry.captureException(err);
			this.errors = {"": (err as Error).message};
		} finally {
			this.apiState = RequestState.Received;
		}
	}

	scrollToFinalPage() {
		const block = this.isShowSupportedTeamCta ? "end" : "start";
		this.scrollToNextScreen(this.finalScreenID, block);
	}

	@action setViewedQuestion(questionID: number) {
		this.viewedQuestionID = questionID;
	}

	@computed get isLogoVisible() {
		const viewedQuestionID = this.viewedQuestionID || this.allItems[0].id;
		if (viewedQuestionID === this.finalScreenID) {
			return true;
		}
		if (viewedQuestionID === this.resultScreenID) {
			return false;
		}
		return this.allItemsByID[viewedQuestionID].type !== QuestionType.BUTTON;
	}

	@action resetProgress() {
		window.scrollTo({
			top: 0,
			left: 0,
			behavior: "smooth",
		});

		this.answers = {};
		this.isShowResultPage = false;
		this.apiState = RequestState.IDLE;
	}
}
